import { createResourceQuery } from '@faro/resource-query';
import { StudyEntityType } from '@faro/study-designer-model';
import { AgenticArtifactReference, StudyDefinitionReference, StudySpace } from '@faro/study-space-service-model';
import { UserInfo } from '@faro/user-management-model';
import { AxiosError } from 'axios';
import { uniq, uniqBy } from 'lodash';
import { ServiceContextValue } from '../../../context';

export interface SpacePageData {
    space: StudySpace;
    studyDefinitionReferences: StudyDefinitionReference[];
    userInfos: UserInfo[];
    publishedArtifacts: AgenticArtifactReference[];
}
export interface SpacePageDataQuery {
    id: string;
    includeDeleted?: boolean;
}

export interface SpacePageResolverArgs {
    context: ServiceContextValue;
    query: SpacePageDataQuery;
}

export const useSpacePageData = createResourceQuery<SpacePageData, SpacePageDataQuery, ServiceContextValue, AxiosError>(
    {
        id: 'spacePageData',
        resolve: async ({ context, query }: SpacePageResolverArgs) => {
            const { studySpaceService, userInfoService, studyDefinitionReferenceService } = context;
            const { id, includeDeleted } = query;
            const [space, studyDefinitionReferences] = await Promise.all([
                studySpaceService.get({ id }),
                studyDefinitionReferenceService.list({
                    spaceId: id,
                    includeDeleted,
                }),
            ]);

            const publishedArtifacts = space.publishedArtifacts ?? [];
            const definitionUserIds = uniqBy(studyDefinitionReferences, 'lastUpdatedBy')
                .map(s => s.lastUpdatedBy)
                .filter(lastUpdatedBy => lastUpdatedBy != null);
            const publishedByIds = publishedArtifacts
                .map(artifact => artifact.publishedBy)
                .filter(publishedBy => publishedBy != null);
            const userIds = uniq([...definitionUserIds, ...publishedByIds]);

            const hasIds = userIds.length > 0;
            const userInfos = hasIds ? await userInfoService.getUserInfoByIds(userIds) : [];

            return {
                space,
                studyDefinitionReferences,
                userInfos,
                publishedArtifacts,
            };
        },
        queryKeys: [StudyEntityType.Study],
    }
);
